<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">{{ pageTitle }}</h1>
      <div class="header-actions">
        <button class="btn btn-secondary" @click="handleBack">
          <i class="ri-arrow-left-line"></i>
          返回
        </button>
        <button v-if="template" class="btn btn-secondary" @click="showReferencesModal = true">
          <i class="ri-links-line"></i>
          查看引用
          <span v-if="referenceActiveCount > 0" class="reference-badge">{{ referenceActiveCount }}</span>
        </button>
        <button v-if="template" class="btn btn-secondary" @click="showExportModal = true">
          <i class="ri-download-line"></i>
          匯出用券名單
        </button>
        <button v-if="template" class="btn btn-primary" @click="handleBatchIssue">
          <i class="ri-mail-send-line"></i>
          批次發放
        </button>
        <button v-if="template" class="btn btn-success" @click="handleEdit">
          <i class="ri-edit-line"></i>
          編輯
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="handleBack">返回</button>
    </div>

    <div v-else-if="template" class="detail-container">
      <!-- 統計卡片 -->
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-icon">
            <i class="ri-gift-line"></i>
          </div>
          <div class="stat-content">
            <div class="stat-label">總發放數</div>
            <div class="stat-value">{{ template.totalIssued || 0 }}</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon success">
            <i class="ri-checkbox-circle-line"></i>
          </div>
          <div class="stat-content">
            <div class="stat-label">總使用數</div>
            <div class="stat-value">{{ template.totalUsed || 0 }}</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon warning">
            <i class="ri-time-line"></i>
          </div>
          <div class="stat-content">
            <div class="stat-label">總過期數</div>
            <div class="stat-value">{{ template.totalExpired || 0 }}</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon info">
            <i class="ri-percent-line"></i>
          </div>
          <div class="stat-content">
            <div class="stat-label">使用率</div>
            <div class="stat-value">{{ usageRate }}%</div>
          </div>
        </div>
      </div>

      <!-- Tab 切換 -->
      <div class="tabs">
        <button
          :class="['tab-button', { active: activeTab === 'basic' }]"
          @click="activeTab = 'basic'"
        >
          <i class="ri-file-list-line"></i>
          基本資訊
        </button>
        <button
          :class="['tab-button', { active: activeTab === 'issues' }]"
          @click="activeTab = 'issues'"
        >
          <i class="ri-gift-line"></i>
          發放記錄
        </button>
      </div>

      <!-- Tab 內容 -->
      <div class="tab-content">
        <!-- 基本資訊 -->
        <div v-if="activeTab === 'basic'" class="detail-section">
          <div class="info-grid">
            <div class="info-item">
              <label>模板名稱</label>
              <div>{{ template.templateName }}</div>
            </div>
            <div class="info-item">
              <label>優惠券類型</label>
              <div>
                <span :class="getCouponTypeBadgeClass(template.couponType)">
                  {{ getCouponTypeText(template.couponType) }}
                </span>
              </div>
            </div>
            <div class="info-item">
              <label>折扣值</label>
              <div>{{ formatDiscountValue(template) }}</div>
            </div>
            <div class="info-item">
              <label>最低消費門檻</label>
              <div>{{ formatCurrency(template.minPurchaseAmount) }}</div>
            </div>
            <div class="info-item">
              <label>有效期</label>
              <div>{{ formatValidityPeriod(template) }}</div>
            </div>
            <div class="info-item">
              <label>每張券可使用次數</label>
              <div>1 次（固定）</div>
            </div>
            <div class="info-item">
              <label>發行數量總上限</label>
              <div>{{ template.totalIssueLimit ? `${template.totalIssueLimit} 張` : '不限制' }}</div>
            </div>
            <div class="info-item">
              <label>是否可分享</label>
              <div>
                <span :class="template.transferable ? 'badge badge-success' : 'badge badge-secondary'">
                  {{ template.transferable ? '是' : '否' }}
                </span>
              </div>
            </div>
            <div class="info-item">
              <label>是否可轉送</label>
              <div>
                <span :class="template.giftable ? 'badge badge-success' : 'badge badge-secondary'">
                  {{ template.giftable ? '是' : '否' }}
                </span>
              </div>
            </div>
            <div class="info-item">
              <label>允許訪客核銷</label>
              <div>
                <span :class="template.allowGuestRedemption ? 'badge badge-success' : 'badge badge-secondary'">
                  {{ template.allowGuestRedemption ? '是' : '否' }}
                </span>
              </div>
            </div>
            <div class="info-item">
              <label>狀態</label>
              <div>
                <span :class="template.isActive ? 'badge badge-success' : 'badge badge-secondary'">
                  {{ template.isActive ? '啟用' : '停用' }}
                </span>
              </div>
            </div>
            <div class="info-item">
              <label>建立時間</label>
              <div>{{ formatDateTime(template.createdAt) }}</div>
            </div>
            <div class="info-item">
              <label>最後更新</label>
              <div>{{ formatDateTime(template.updatedAt) }}</div>
            </div>
          </div>
        </div>

        <!-- 發放記錄 -->
        <div v-if="activeTab === 'issues'" class="detail-section">
          <!-- 篩選器 -->
          <div class="filters">
            <div class="filter-group">
              <label>狀態</label>
              <select v-model="issueFilters.status" @change="handleIssueFilterChange" class="form-control">
                <option value="">全部</option>
                <option value="unused">未使用</option>
                <option value="used">已使用</option>
                <option value="expired">已過期</option>
                <option value="cancelled">已取消</option>
                <option value="deleted">已刪除</option>
              </select>
            </div>
            <div class="filter-group">
              <label>搜尋會員</label>
              <input
                v-model="issueSearchQuery"
                type="text"
                placeholder="輸入會員姓名、電話或 Email"
                class="form-control"
                @input="handleIssueSearch"
              />
            </div>
          </div>

          <!-- 載入狀態 -->
          <div v-if="issuesLoading" class="loading-state">
            <div class="spinner"></div>
            <p>載入中...</p>
          </div>

          <!-- 錯誤狀態 -->
          <div v-else-if="issuesError" class="error-state">
            <p class="error-message">{{ issuesError }}</p>
          </div>

          <!-- 發放記錄表格 -->
          <div v-else class="table-container">
            <table class="data-table">
              <thead>
                <tr>
                  <th>優惠券代碼</th>
                  <th>會員資訊</th>
                  <th>發放時間</th>
                  <th>有效期</th>
                  <th>狀態</th>
                  <th>使用時間</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="issues.length === 0">
                  <td colspan="7" class="text-center">尚無發放記錄</td>
                </tr>
                <tr v-for="issue in issues" :key="issue.id">
                  <td>
                    <code class="coupon-code">{{ issue.couponCode }}</code>
                  </td>
                  <td>
                    <div v-if="issue.memberId">
                      <div class="member-name">{{ issue.memberName || '-' }}</div>
                      <div class="member-info">
                        <span v-if="issue.memberPhone">{{ issue.memberPhone }}</span>
                        <span v-if="issue.memberEmail">{{ issue.memberEmail }}</span>
                      </div>
                    </div>
                    <span v-else class="text-muted">訪客</span>
                  </td>
                  <td>{{ formatDateTime(issue.issuedAt) }}</td>
                  <td>
                    <div v-if="issue.validFrom && issue.validUntil">
                      {{ formatDate(issue.validFrom) }} ~ {{ formatDate(issue.validUntil) }}
                    </div>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td>
                    <span :class="getStatusBadgeClass(issue.status)">
                      {{ getStatusText(issue.status) }}
                    </span>
                  </td>
                  <td>
                    <span v-if="issue.usedAt">{{ formatDateTime(issue.usedAt) }}</span>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td>
                    <button
                      class="btn btn-sm btn-primary"
                      @click="handleViewIssue(issue)"
                    >
                      查看詳情
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- 分頁 -->
            <ListPagination
              v-if="issuesTotal > 0"
              :page="issuesCurrentPage"
              :total-pages="issuesTotalPages"
              :total="issuesTotal"
              @change="goToIssuesPage"
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  <CouponTemplateReferencesModal
    :open="showReferencesModal"
    :template-id="route.params.id"
    @close="showReferencesModal = false"
  />

  <CouponExportModal
    :visible="showExportModal"
    :params="{ templateId: route.params.id }"
    @close="showExportModal = false"
  />
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { couponService } from '@/api/modules/coupons.js';
import { showError } from '@/utils/swal.js';
import CouponTemplateReferencesModal from '@/components/CouponTemplateReferencesModal.vue';
import CouponExportModal from '@/components/CouponExportModal.vue';

export default {
  name: 'CouponTemplateDetail',
  components: { CouponTemplateReferencesModal, CouponExportModal },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const { pageTitle } = usePageTitle('優惠券模板詳情');

    const template = ref(null);
    const loading = ref(false);
    const error = ref(null);
    const activeTab = ref('basic');

    // 發放記錄相關狀態
    const issues = ref([]);
    const issuesLoading = ref(false);
    const issuesError = ref(null);
    const issuesCurrentPage = ref(1);
    const issuesPageSize = ref(20);
    const issuesTotal = ref(0);
    const issueFilters = ref({
      status: ''
    });
    const issueSearchQuery = ref('');
    const issueSearchTimeout = ref(null);
    const showReferencesModal = ref(false);
    const referenceActiveCount = ref(0);
    const showExportModal = ref(false);

    const usageRate = computed(() => {
      if (!template.value || !template.value.totalIssued) return 0;
      return ((template.value.totalUsed / template.value.totalIssued) * 100).toFixed(1);
    });

    const issuesTotalPages = computed(() => {
      return Math.ceil(issuesTotal.value / issuesPageSize.value);
    });

    const fetchReferenceSummary = async () => {
      try {
        const response = await couponService.getTemplateReferences(route.params.id);
        if (response?.success && response.data) {
          referenceActiveCount.value = response.data.activeCount || 0;
        }
      } catch (err) {
        console.error('查詢優惠券模板引用摘要失敗:', err);
      }
    };

    const fetchTemplate = async () => {
      loading.value = true;
      error.value = null;

      try {
        const response = await couponService.getTemplate(route.params.id);

        if (response && response.success && response.data) {
          template.value = response.data;
        } else {
          error.value = response?.message || '獲取優惠券模板失敗';
        }
      } catch (err) {
        console.error('獲取優惠券模板失敗:', err);
        error.value = err.message || '獲取優惠券模板失敗';
        showError(error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleBack = () => {
      router.back();
    };

    const handleEdit = () => {
      router.push(`/client/coupons/templates/${route.params.id}/edit`);
    };

    const handleBatchIssue = () => {
      router.push({
        path: '/client/batch-operations/create',
        query: {
          operationType: 'issue_coupons',
          templateId: route.params.id
        }
      });
    };

    const formatDateTime = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const formatCurrency = (amount) => {
      if (amount === null || amount === undefined) return '$0';
      return `$${Number(amount).toLocaleString('zh-TW')}`;
    };

    const formatDiscountValue = (template) => {
      if (template.couponType === 'discount') {
        return `${template.discountValue}%`;
      } else if (template.couponType === 'fixed_amount') {
        return formatCurrency(template.discountValue);
      } else if (template.couponType === 'free_shipping') {
        return '免運費';
      } else if (template.couponType === 'gift') {
        return '贈品';
      }
      return '-';
    };

    const formatValidityPeriod = (template) => {
      if (template.validityDays) {
        return `${template.validityDays} 天`;
      } else if (template.fixedStartDate && template.fixedEndDate) {
        return `${template.fixedStartDate} ~ ${template.fixedEndDate}`;
      }
      return '-';
    };

    const getCouponTypeText = (type) => {
      const typeMap = {
        discount: '折扣券',
        fixed_amount: '折價券',
        free_shipping: '運費券',
        gift: '贈品券'
      };
      return typeMap[type] || type;
    };

    const getCouponTypeBadgeClass = (type) => {
      const classMap = {
        discount: 'badge badge-primary',
        fixed_amount: 'badge badge-success',
        free_shipping: 'badge badge-info',
        gift: 'badge badge-warning'
      };
      return classMap[type] || 'badge badge-secondary';
    };

    // 發放記錄相關方法
    const fetchIssues = async () => {
      if (!template.value) return;

      issuesLoading.value = true;
      issuesError.value = null;

      try {
        const params = {
          page: issuesCurrentPage.value,
          pageSize: issuesPageSize.value
        };

        if (issueFilters.value.status) {
          params.status = issueFilters.value.status;
        }

        const response = await couponService.getTemplateIssues(route.params.id, params);

        if (response && response.success && response.data) {
          if (response.data.items) {
            issues.value = response.data.items || [];
            issuesTotal.value = response.data.pagination?.totalItems || 0;
          } else if (Array.isArray(response.data)) {
            issues.value = response.data;
            issuesTotal.value = response.data.length;
          }
        } else {
          issuesError.value = response?.message || '獲取發放記錄失敗';
        }
      } catch (err) {
        console.error('獲取發放記錄失敗:', err);
        issuesError.value = err.message || '獲取發放記錄失敗';
      } finally {
        issuesLoading.value = false;
      }
    };

    const handleIssueFilterChange = () => {
      issuesCurrentPage.value = 1;
      fetchIssues();
    };

    const handleIssueSearch = () => {
      if (issueSearchTimeout.value) {
        clearTimeout(issueSearchTimeout.value);
      }
      issueSearchTimeout.value = setTimeout(() => {
        issuesCurrentPage.value = 1;
        // 注意：目前後端 API 不支援會員搜尋，這裡先保留邏輯
        fetchIssues();
      }, 500);
    };

    const goToIssuesPage = (page) => {
      if (page >= 1 && page <= issuesTotalPages.value) {
        issuesCurrentPage.value = page;
        fetchIssues();
      }
    };

    const handleViewIssue = (issue) => {
      router.push(`/client/coupons/issues/${issue.id}`);
    };

    const getStatusText = (status) => {
      const statusMap = {
        available: '可用',
        used: '已使用',
        expired: '已過期',
        cancelled: '已取消'
      };
      return statusMap[status] || status;
    };

    const getStatusBadgeClass = (status) => {
      const classMap = {
        available: 'badge badge-success',
        used: 'badge badge-primary',
        expired: 'badge badge-secondary',
        cancelled: 'badge badge-danger'
      };
      return classMap[status] || 'badge badge-secondary';
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    };

    // 監聽 tab 切換，載入發放記錄
    const watchActiveTab = () => {
      if (activeTab.value === 'issues' && issues.value.length === 0 && !issuesLoading.value) {
        fetchIssues();
      }
    };

    onMounted(() => {
      fetchTemplate();
      fetchReferenceSummary();
    });

    // 監聽 tab 切換
    watch(activeTab, (newTab) => {
      if (newTab === 'issues' && template.value) {
        fetchIssues();
      }
    });

    return {
      route,
      template,
      loading,
      error,
      activeTab,
      usageRate,
      pageTitle,
      showReferencesModal,
      referenceActiveCount,
      showExportModal,
      handleBack,
      handleEdit,
      handleBatchIssue,
      formatDateTime,
      formatCurrency,
      formatDiscountValue,
      formatValidityPeriod,
      getCouponTypeText,
      getCouponTypeBadgeClass,
      // 發放記錄相關
      issues,
      issuesLoading,
      issuesError,
      issuesCurrentPage,
      issuesTotal,
      issuesTotalPages,
      issueFilters,
      issueSearchQuery,
      handleIssueFilterChange,
      handleIssueSearch,
      goToIssuesPage,
      handleViewIssue,
      getStatusText,
      getStatusBadgeClass,
      formatDate
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_detail-common.scss';

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.reference-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 999px;
  background: #fef3c7;
  color: #b45309;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f0f0;
  color: #666;
  font-size: 24px;
}

.stat-icon.success {
  background: #d4edda;
  color: #28a745;
}

.stat-icon.info {
  background: #d1ecf1;
  color: #17a2b8;
}

.stat-icon.warning {
  background: #fff3cd;
  color: #ffc107;
}

.stat-content {
  flex: 1;
}

.stat-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

.tabs {
  display: flex;
  gap: 4px;
  border-bottom: 2px solid #e0e0e0;
  margin-bottom: 24px;
}

.tab-button {
  padding: 12px 24px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  font-size: 16px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.tab-button:hover {
  color: #333;
}

.tab-button.active {
  color: #007bff;
  border-bottom-color: #007bff;
  font-weight: 500;
}

.tab-content {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.info-item {
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.info-item label {
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.info-item div {
  font-size: 16px;
  color: #333;
}

.info-text {
  text-align: center;
  color: #666;
  padding: 40px;
}

/* 發放記錄樣式 */
.filters {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.filter-group {
  flex: 1;
  min-width: 200px;
}

.filter-group label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
}

.table-container {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.data-table thead {
  background: #f8f9fa;
}

.data-table th {
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  color: #333;
  border-bottom: 2px solid #e0e0e0;
}

.data-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
  color: #666;
}

.data-table tbody tr:hover {
  background: #f8f9fa;
}

.coupon-code {
  background: #f0f0f0;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #333;
}

.member-name {
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.member-info {
  font-size: 12px;
  color: #666;
}

.member-info span {
  margin-right: 8px;
}

.text-center {
  text-align: center;
}

.text-muted {
  color: #999;
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding: 16px 0;
}

.page-info {
  font-size: 14px;
  color: #666;
}
</style>
